import {
    type ContractVariant,
    widgetPunctualityCharttooltipFlightrowContract,
} from 'aos-ds/src/patterns/widget-punctuality-charttooltip-flightrow/widget-punctuality-charttooltip-flightrow.contract'
import { Typography } from 'aos-ds/src/typography/Typography'
import { variantForStyleId } from 'aos-ds/src/typography/variants'
import React from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { withoutSampleWidth } from '../../tailwind/withoutSampleWidth'
import { nativeSingleLine } from '../../webOnly'

export const PUNCTUALITY_CHART_TOOLTIP_USERS =
    widgetPunctualityCharttooltipFlightrowContract.props.user.values

export type PunctualityChartTooltipUser = (typeof PUNCTUALITY_CHART_TOOLTIP_USERS)[number]

export type PunctualityChartTooltipFlight = {
    flight: string
    delay: string
    codes?: string
}

export type PunctualityChartTooltipFlightRowProps = PunctualityChartTooltipFlight & {
    user: PunctualityChartTooltipUser
    className?: string
    testID?: string
}

export const resolvePunctualityChartTooltipFlightRow = (
    user: PunctualityChartTooltipUser,
): ContractVariant => {
    const key = `user=${user}`
    const cell = (
        widgetPunctualityCharttooltipFlightrowContract.variants as Record<string, ContractVariant>
    )[key]
    if (!cell) {
        throw new Error(`PunctualityChartTooltipFlightRow: the Figma contract has no "${key}"`)
    }
    return cell
}

type TextPart = { className: string; variant: ReturnType<typeof variantForStyleId> }

const textPart = (cell: ContractVariant, name: string): TextPart => {
    const className = cell.parts[name]
    const style = cell.partTextStyles[name]
    if (className === undefined || style === undefined) {
        throw new Error(
            `PunctualityChartTooltipFlightRow: the Figma contract has no part "${name}"`,
        )
    }
    return { className, variant: variantForStyleId(style) }
}

/**
 * Figma `Widget/Punctuality/ChartTooltip/FlightRow` (19022:458356): one
 * not-punctual flight in the chart tooltip — flight number, delay and, for
 * `user=finavia`, the delay codes. `user=external` has no codes part, so the
 * codes are not drawn for it whatever the caller passes. The row fills its
 * direction; the frame width is the sample Figma drew.
 */
export const PunctualityChartTooltipFlightRow = ({
    user,
    flight,
    delay,
    codes,
    className,
    testID = 'punctuality-chart-tooltip-flight-row',
}: PunctualityChartTooltipFlightRowProps) => {
    const cell = resolvePunctualityChartTooltipFlightRow(user)
    const flightPart = textPart(cell, 'flight')
    const delayPart = textPart(cell, 'delay')
    const codesPart = cell.parts.codes === undefined ? null : textPart(cell, 'codes')

    return (
        <View
            className={twMerge('w-full flex-row', withoutSampleWidth(cell.container), className)}
            testID={testID}
        >
            <Typography
                variant={flightPart.variant}
                className={twMerge('shrink-0', cell.label, flightPart.className)}
                testID={`${testID}-flight`}
            >
                {flight}
            </Typography>
            <Typography
                variant={delayPart.variant}
                className={twMerge('min-w-0 flex-1', delayPart.className)}
                testID={`${testID}-delay`}
            >
                {delay}
            </Typography>
            {codesPart && Boolean(codes) && (
                <Typography
                    variant={codesPart.variant}
                    className={twMerge('shrink-0 whitespace-nowrap', codesPart.className)}
                    numberOfLines={nativeSingleLine()}
                    testID={`${testID}-codes`}
                >
                    {codes}
                </Typography>
            )}
        </View>
    )
}

PunctualityChartTooltipFlightRow.displayName = 'PunctualityChartTooltipFlightRow'
